/**
 * Action Holder Agreements Component
 *
 * Displays open agreements where the employee is the action holder.
 * Shows on employee dashboard for agreements requiring their follow-up.
 */

import { useState } from 'react';
import { useActionHolderAgreements } from './useAgreements';
import { AgreementDetailModal } from './AgreementDetailModal';
import type { ScoreAgreement } from '../../lib/evaluationsApi';

interface ActionHolderAgreementsProps {
  employeeId: number;
}

const STATUS_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  concept: { label: 'Concept', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  definitief: { label: 'Definitief', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  afgerond: { label: 'Afgerond', color: 'text-green-700', bgColor: 'bg-green-100' },
  geannuleerd: { label: 'Geannuleerd', color: 'text-red-700', bgColor: 'bg-red-100' },
};

function isDeadlineUrgent(deadline: string | null): boolean {
  if (!deadline) return false;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffDays = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays <= 7 && diffDays >= 0;
}

function isDeadlinePassed(deadline: string | null): boolean {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

export function ActionHolderAgreements({ employeeId }: ActionHolderAgreementsProps) {
  const { data, isLoading, error } = useActionHolderAgreements(employeeId);
  const [selectedAgreement, setSelectedAgreement] = useState<ScoreAgreement | null>(null);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Mijn Openstaande Afspraken</h3>
        </div>
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
          <span className="ml-3 text-gray-500">Laden...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Mijn Openstaande Afspraken</h3>
        </div>
        <div className="p-6">
          <p className="text-red-600 text-sm">Er is een fout opgetreden bij het laden van afspraken.</p>
        </div>
      </div>
    );
  }

  const agreements = data?.data || [];

  if (agreements.length === 0) {
    return null; // Don't show section if no action holder agreements
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-amber-200 shadow-sm">
        <div className="px-6 py-4 border-b border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            <h3 className="text-lg font-medium text-amber-800">
              Mijn Openstaande Afspraken
              <span className="ml-2 px-2 py-0.5 text-sm bg-amber-200 text-amber-800 rounded-full">
                {agreements.length}
              </span>
            </h3>
          </div>
          <p className="mt-1 text-sm text-amber-600">
            Afspraken waar u verantwoordelijk bent voor de opvolging
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {agreements.map((agreement) => {
            const statusConfig = STATUS_LABELS[agreement.status] || STATUS_LABELS.concept;
            const urgent = isDeadlineUrgent(agreement.deadline);
            const passed = isDeadlinePassed(agreement.deadline);

            return (
              <div
                key={agreement.id}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setSelectedAgreement(agreement)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {agreement.agreementTypeName && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          {agreement.agreementTypeIcon} {agreement.agreementTypeName}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig?.bgColor || 'bg-gray-100'} ${statusConfig?.color || 'text-gray-700'}`}>
                        {statusConfig?.label || agreement.status}
                      </span>
                      {agreement.deadline && (
                        <span className={`text-xs ${passed ? 'text-red-600 font-medium' : urgent ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                          {passed ? '⚠️ ' : urgent ? '⏰ ' : ''}
                          Deadline: {new Date(agreement.deadline).toLocaleDateString('nl-NL')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-2">{agreement.beschrijving}</p>
                    {agreement.evaluationPeriod && (
                      <p className="mt-1 text-xs text-gray-400">
                        Beoordeling: {new Date(agreement.evaluationPeriod.start).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })} - {new Date(agreement.evaluationPeriod.end).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedAgreement && (
        <AgreementDetailModal
          agreementId={selectedAgreement.id}
          onClose={() => setSelectedAgreement(null)}
          canEdit={true}
        />
      )}
    </>
  );
}
